import { MenuItem, TextField } from "@mui/material"
import { ReactElement, useState, useEffect } from "react"
import { useSnackbar } from "notistack"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { toDineroOrUndefined } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { Order } from "../../../controlKitchenTypes"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { refundOrder } from "../../../../receipt/ordersApi"
import { StandardSuccessDialog } from "../../../../../shared/components/dialogs/StandardSuccessDialog"
import {
    StandardErrorDialog,
    useStandardErrorDialogState,
} from "../../../../../shared/components/dialogs/StandardErrorDialog"
import { useLabelPrinter } from "../../../../serviceCenter/report/home/hooks/useLabelPrinter"
import { TakeawayLabelData } from "../../../../serviceCenter/report/external/brotherPrinter"
import { useDateTime } from "../../../../localization/useDateTime"
import { LabelPrinterExtensionDialog } from "../../../../../shared/components/dialogs/LabelPrinterExtensionDialog"
import { ProductionOrderTypeFilter } from "../pages/components/PosTransactionsTable"
import { ApiError } from "../../../../../core/api/errorTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

type OrderActionMenuProps = Readonly<{
    order: Order
    orders?: Order[]
    onSuccess?: () => void
    closeMenu?: () => void
    orderType?: ProductionOrderTypeFilter
}>

export function OrderActionMenu({ order, orders, onSuccess, closeMenu, orderType }: OrderActionMenuProps) {
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showLabelCopiesModal, setShowLabelCopiesModal] = useState(false)
    const [labelCopies, setLabelCopies] = useState(1)
    const { errorDialog: showErrorDialog, openErrorDialog, closeErrorDialog } = useStandardErrorDialogState()
    const [receipt, setReceipt] = useState<Receipt>()

    const { isInstalledError, clearState, printMultipleTakeawayLabels, hasError, printErrorMessage } = useLabelPrinter()
    const { enqueueSnackbar } = useSnackbar()

    // Show snackbar when bpac error occurs
    useEffect(() => {
        if (hasError && printErrorMessage) {
            enqueueSnackbar(printErrorMessage, { variant: "error" })
            clearState()
        }
    }, [hasError, printErrorMessage, enqueueSnackbar, clearState])

    const getOrderItems = (orderData: Order): string => {
        const orderLines = orderData.orderLines ?? []
        return orderLines.map((line) => `${line.items} x ${line.name}`).join(", ")
    }

    const handlePrintLabel = async (copies: number) => {
        const deliveryTime = order.deliveryDetails?.time ?? order.deliveries?.[0]?.deliveryTime
        const labelData: TakeawayLabelData = {
            orderId: order.id,
            customerName: order.customer?.displayName || "",
            deliveryDate: deliveryTime ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(deliveryTime)) : "",
            numberOfItems: order.lineNumber,
            description: getOrderItems(order),
            copies,
        }
        await printMultipleTakeawayLabels([labelData])
    }

    const handleOpenLabelCopiesModal = () => {
        setLabelCopies(1)
        setShowLabelCopiesModal(true)
    }

    const handleConfirmLabelCopies = () => {
        setShowLabelCopiesModal(false)
        handlePrintLabel(labelCopies)
    }

    const { mutateAsync: callRefundOrder, isLoading: isRefunding } = useApiMutation(refundOrder, {
        async onSuccess(_params, _data) {
            setShowConfirmModal(false)
            setShowSuccessModal(true)
        },
        async onError(_, error) {
            setShowConfirmModal(false)
            const displayMessage = error instanceof ApiError && error.hasApiMessage ? error.displayMessage : undefined
            openErrorDialog({
                title: l10n.getString("transactions-table-list-refund"),
                description: displayMessage || l10n.getString("transactions-table-list-refund-error-message"),
            })
        },
    })

    const handleRefundClick = () => {
        setShowConfirmModal(true)
    }

    const handleConfirmRefund = () => {
        callRefundOrder({ orderId: order.id })
    }

    const handleShowReceipt = () => {
        setReceipt({
            orderId: order.id,
            orderUid: order.uid,
            orderType: order.orderType,
        })
    }

    const onPrinterClose = () => {
        clearState()
        closeMenu?.()
        setLabelCopies(1)
    }

    const amountDisplay = (() => {
        const dineroAmount = toDineroOrUndefined(order.amount)
        return dineroAmount ? moneyFactory.format(dineroAmount) : ""
    })()

    const menuItems: ReactElement[] = []

    menuItems.push(
        <LocalizedStrict id="office-purchase-receipt" key={`receipt-${order.id}`}>
            <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
        </LocalizedStrict>,
    )

    if (order.orderAction?.refundOrder?.allow) {
        menuItems.push(
            <LocalizedStrict id="order-action-menu-refund" key={`refund-${order.id}`}>
                <MenuItem onClick={handleRefundClick}>Refund</MenuItem>
            </LocalizedStrict>,
        )
    }

    if (orderType === ProductionOrderTypeFilter.TAKEAWAY) {
        menuItems.push(
            <LocalizedStrict id="order-action-menu-print-label" key={`print-label-${order.id}`}>
                <MenuItem onClick={handleOpenLabelCopiesModal}>Print label</MenuItem>
            </LocalizedStrict>,
        )
    }

    return (
        <>
            {menuItems}

            <LocalizedStrict
                id="transactions-table-list-refund"
                attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                vars={{
                    orderId: order.id.toString(),
                    amount: amountDisplay,
                }}
            >
                <ConfirmModal
                    open={showConfirmModal}
                    onClose={() => setShowConfirmModal(false)}
                    title="Refund"
                    message="Are you sure you want to refund order?"
                    handleClick={handleConfirmRefund}
                    buttonLabelSubmit="Confirm"
                    buttonLabelCancel="Cancel"
                    buttonSubmitDisabled={isRefunding}
                    autoCloseAfterSubmit={false}
                />
            </LocalizedStrict>

            {showSuccessModal && (
                <StandardSuccessDialog
                    open={showSuccessModal}
                    dialogType="refundOrder"
                    onClose={() => {
                        setShowSuccessModal(false)
                        onSuccess?.()
                    }}
                />
            )}

            {showErrorDialog.open && (
                <StandardErrorDialog
                    dialogDetails={showErrorDialog}
                    onClose={() => {
                        closeErrorDialog()
                        onSuccess?.()
                    }}
                />
            )}

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={orders}
                    open={!!receipt}
                    onClose={() => setReceipt(undefined)}
                />
            )}

            <LocalizedStrict
                id="print-label-copies-modal"
                attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
            >
                <ConfirmModal
                    open={showLabelCopiesModal}
                    onClose={() => setShowLabelCopiesModal(false)}
                    title="Print label"
                    message="How many copies do you want to print?"
                    handleClick={handleConfirmLabelCopies}
                    buttonLabelSubmit="Print"
                    buttonLabelCancel="Cancel"
                    buttonColorSubmit="black"
                    autoCloseAfterSubmit={false}
                >
                    <TextField
                        type="number"
                        value={labelCopies}
                        onChange={(e) => setLabelCopies(parseInt(e.target.value))}
                        inputProps={{ min: 1 }}
                        fullWidth
                        autoFocus
                    />
                </ConfirmModal>
            </LocalizedStrict>

            <LabelPrinterExtensionDialog open={isInstalledError} onClose={onPrinterClose} />
        </>
    )
}
